// Lens order lines are STORED under the supplier/real name (e.g. "optimax")
// because lens stock matching and the central-warehouse lookup key off it.
// Nobody in the shop should see that name though — the salesperson and the
// client both work with the store's own commercial name (e.g. "Light Sky").
//
// So `item.name` stays the real name everywhere, and every place that renders
// a lens line goes through here instead of reading `item.name` directly.

import i18n from '../i18n';

export function isLensItem(item) {
  return (item?.type || item?.item_type) === 'lens';
}

// Resolution order:
//   1. what the order line carries (set at sale time from the catalog)
//   2. the lens picked in this sales session (covers the summary before the
//      order row exists, and orders saved before commercialName was carried)
//   3. the real name — correct fallback for a lens with no commercial name
//      configured, and for "buyer"/custom products with no catalog entry.
export function lensDisplayName(item) {
  const fromItem = item?.specifications?.commercialName || item?.commercialName;
  if (fromItem) return fromItem;
  try {
    const selected = JSON.parse(sessionStorage.getItem('selectedLens') || 'null');
    if (selected?.commercialName) {
      // Only trust the session lens if it is the one on this line.
      const sameLens = !item?.name
        || String(selected.name || '').trim().toLowerCase() === String(item.name).trim().toLowerCase();
      if (sameLens) return selected.commercialName;
    }
  } catch (_) { /* ignore */ }
  return item?.name || '';
}

// Display name for any order line: lenses use their commercial name, other
// line types keep whatever they were saved as.
export function itemDisplayName(item) {
  return isLensItem(item) ? lensDisplayName(item) : frameDisplayName(item?.name || '');
}

// The client's own frame is stored under one fixed English string so the order
// row means the same thing to the lab, to central and to a report, whatever
// language the sale was made in. Only the rendering is translated — same rule
// as lensDisplayName above, and it also covers orders saved before this
// existed, since they carry the identical canonical string.
export const OWN_FRAME_NAME = "Client's Own Frame";

export function isOwnFrameName(name) {
  return String(name || '').trim().toLowerCase() === OWN_FRAME_NAME.toLowerCase();
}

/** Display name for a frame line: translated only for the own-frame marker. */
export function frameDisplayName(nameOrItem) {
  const name = typeof nameOrItem === 'string' ? nameOrItem : (nameOrItem?.name || '');
  return isOwnFrameName(name) ? i18n.t('common.clientsOwnFrame') : name;
}
